import Badge from "@mui/material/Badge";
import React, { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Divider, Menu, MenuItem } from "@mui/material";
import ConfirmationModal from "../Modals/ConfirmationModal";
import PropTypes from "prop-types";
import { isPlainObject } from "../../Helpers/ObjectHelper";

const ActionButton = (props) => {
    let [anchorEl, setAnchorEl] = useState(null);
    let [hasBeenClicked, setHasBeenClicked] = useState(false);
    let [isConfirmationOpen, setConfirmationOpen] = React.useState(false);
    let [menuItemSelected, setMenuItemSelected] = useState(false);

    useEffect(() => {
        setHasBeenClicked(false);
    }, [props.isDisabled]);

    const handleButtonClick = (event) => {

        if (props.menuItems || props.children) {
            event.preventDefault()
            setAnchorEl(event.currentTarget)

        } else if (props.confirmationText) {
            event.preventDefault()
            setConfirmationOpen(true);

        } else if (props.onClick) {
            event.preventDefault()
            if (!(props.preventMultipleClicks && hasBeenClicked)) {
                props.onClick(event);
            }

        } else {
            // Do nothing and follow the url in href as normal!
        }

        setHasBeenClicked(true);
    }

    const handleConfirmationClick = (modalProps, event) => {
        if (event) {
            event.preventDefault();
        }

        if (props.menuItems || props.children) {
            props.onMenuItemClick(menuItemSelected);
        } else {
            props.onClick(event);
        }
        setConfirmationOpen(false);
    }

    const handleConfirmationCancelClick = (event) => {
        resetButton();
    }

    const resetButton = () => {
        setAnchorEl(null);
        setHasBeenClicked(false);
        setConfirmationOpen(false);
    }

    const getIcon = () => {
        if ((typeof props.isActive === 'undefined')
            && props.animateOnClick
            && hasBeenClicked
        ) {
            return (<i className={`fa fa-spinner fa-pulse`}></i>);
        }

        if (props.isActive && props.animateOnClick) {
            return (<i className={`fa fa-spinner fa-pulse`}></i>);
        }

        if (props.icon) {
            return (<i className={`fa ${ props.icon}`}></i>);
        }
        return null;
    }

    const getIconStart = () => {
        if (props.iconPosition === 'start') {
            return getIcon();
        }
    }

    const getIconEnd = () => {
        if (props.iconPosition === 'end') {
            return getIcon();
        }
    }

    const getLabelRight = () => {
        if (typeof props.labelRight !== 'undefined') {
            return <span className="label-right">{props.labelRight}</span>
        }
    }

    const getConfirmationTitle = () => {
        const defaultTitle = 'Confirm Action';

        if (props.confirmationText.hasOwnProperty('title')) {
            return props.confirmationText.title ?? defaultTitle;
        }

        if (menuItemSelected) {
            if (Array.isArray(props.confirmationText)) {
                let textIndex = (menuItemSelected >= 0) ? menuItemSelected : 0;
                return props.confirmationText[textIndex].title ?? defaultTitle
            }

            if (isPlainObject(props.confirmationText)
                && !props.confirmationText.hasOwnProperty('title')
            ) {
                return props.confirmationText[menuItemSelected].title ?? defaultTitle;
            }
        }

        if (isPlainObject(props.confirmationText)) {
            const confirmTitleOptions = Object.values(props.confirmationText);
            if (confirmTitleOptions.length && confirmTitleOptions[0].hasOwnProperty('title')) {
                return confirmTitleOptions[0].title ?? defaultTitle;
            }
        }

        return defaultTitle;
    }

    const getConfirmationMessage = () => {
        const defaultText = 'Please confirm this action.';

        if (props.confirmationText.hasOwnProperty('text')) {
            return props.confirmationText.text ?? defaultText;
        }

        if (menuItemSelected) {
            if (Array.isArray(props.confirmationText)) {
                let textIndex = (menuItemSelected >= 0) ? menuItemSelected : 0;
                return props.confirmationText[textIndex].text ?? defaultText;
            }

            if (isPlainObject(props.confirmationText)
                && !props.confirmationText.hasOwnProperty('title')
            ) {
                return props.confirmationText[menuItemSelected].text ?? defaultText;
            }
        }

        if (isPlainObject(props.confirmationText)) {
            const confirmTextOptions = Object.values(props.confirmationText);
            if (confirmTextOptions.length && confirmTextOptions[0].hasOwnProperty('text')) {
                return confirmTextOptions[0].text ?? defaultText;
            }
        }

        return defaultText;
    }

    const handleMenuClick = (event, index) => {
        event.preventDefault()

        setMenuItemSelected(index);

        if (props.confirmationText) {
            setConfirmationOpen(true);
        } else {
            resetButton();
            props.onMenuItemClick(index);
        }
    }

    const handleMenuClose = (event) => {
        setAnchorEl(null);
    }

    const getClassNames = () => {
        let classNames = props.className ?? '';

        if (classNames.length < 1) {
            classNames = ' primary';
        }

        if (props.isActive) {
            classNames += ' is-active';
        }

        return classNames;
    }

    let component = (props.url)
        ? (<>
            <a className={`button ${getClassNames()}`}
               onClick={(e) => handleButtonClick(e)}
               href={props.url}
            >
                {getIconStart()}
                {props.title && (
                    <span>{props.title}</span>
                )}
                {getIconEnd()}
                {getLabelRight()}
            </a>
        </>)
        : (<>
            <button className={`button ${getClassNames()}`}
                onClick={(e) => handleButtonClick(e)}
                disabled={props.isDisabled}
            >
                {getIconStart()}
                {props.title && (
                    <span>{props.title}</span>
                )}
                {getIconEnd()}
                {getLabelRight()}
            </button>
        </>)
    ;

    if (props.tooltip) {
        component = (
            <Tooltip title={props.tooltip}>
                <div>{component}</div>
            </Tooltip>
        );
    }

    if (typeof props.badgeContent !== 'undefined') {
        if (props.badgeContent === '') {
            component = (
                <Badge
                    badgeContent={props.badgeContent}
                    variant="dot"
                    color="secondary"
                    sx={{
                        "& .MuiBadge-badge": {
                            color: 'white',
                            backgroundColor: "red"
                        }
                    }}
                >
                    {component}
                </Badge>
            );
        } else {
            component = (
                <Badge
                    badgeContent={props.badgeContent}
                    color="secondary"
                    sx={{
                        "& .MuiBadge-badge": {
                            color: 'white',
                            backgroundColor: "red"
                        }
                    }}
                >
                    {component}
                </Badge>
            );
        }
    }

    if (props.children || props.menuItems) {

        component = (<>
            {component}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
                sx={{
                    '& .MuiPopover-paper': {
                        backgroundColor: props.menuBackgroundColor,
                    },
                    '& .MuiMenuItem-root': {
                        color: props.menuTextColor,
                        backgroundColor: 'transparent',
                        '& i': {
                            color: props.menuTextColor,
                        },
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {(props.children) &&
                    props.children
                }
                {(props.menuItems) &&
                    Object
                        .keys(props.menuItems)
                        .map(key => {
                            if (props.menuItems[key] === 'divider') {
                                return <Divider key={key}/>
                            } else {
                                return <MenuItem
                                    key={key}
                                    onClick={(event) => handleMenuClick(event, key)}
                                    selected={false}
                                >
                                    {props.menuItems[key]}
                                </MenuItem>
                            }
                        })
                }
            </Menu>
        </>);
    }

    if (props.confirmationText) {
        component = (<>
            {component}
            <ConfirmationModal
                title={getConfirmationTitle()}
                message={getConfirmationMessage()}
                open={isConfirmationOpen}
                onConfirm={handleConfirmationClick}
                onCancel={handleConfirmationCancelClick}
                confirmText="Confirm"
                cancelText="Cancel"
                useYesNoConfirmation={props.useYesNoConfirmation}
            />
        </>)
    }

    return component;
}

ActionButton.propTypes = {
    title: PropTypes.string,
    icon: PropTypes.string,
    iconPosition: PropTypes.string,
    className: PropTypes.string,

    children: PropTypes.any,
    menuItems: PropTypes.object,

    url: PropTypes.string,
    tooltip: PropTypes.string,
    confirmationText: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
    badgeContent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    animateOnClick: PropTypes.bool,
    preventMultipleClicks: PropTypes.bool,
    isActive: PropTypes.bool,
    isDisabled: PropTypes.bool,
    useYesNoConfirmation: PropTypes.bool,

    onClick: PropTypes.func,
    onMenuItemClick: PropTypes.func,

    menuBackgroundColor: PropTypes.string,
    menuTextColor: PropTypes.string
}

ActionButton.defaultProps = {
    iconPosition: 'start',
    menuBackgroundColor: '#fff',
    menuTextColor: 'var(--siteconfig-ui-std-txtcolor)',
    useYesNoConfirmation: true,
    preventMultipleClicks: false,
}


export default ActionButton;
